import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import { Button, cn } from '@bliks-io/ui';
import { isForbiddenError } from '@/api/errors';
import { PartnerInfoPanel } from './PartnerInfoPanel';

interface PartnerErrorStateProps {
  error: unknown;
  /** Icon shown on the expected-error panel (not-linked / admin-only / not-found). */
  icon: LucideIcon;
  /** Title of the expected-error panel (pass the already-translated string). */
  title: string;
  description?: string;
  /** Widens the friendly panel beyond 403s (e.g. also treat a 404 as expected). */
  isExpectedError?: (error: unknown) => boolean;
  /** Renders a retry button in the unexpected-error branch. */
  onRetry?: () => void;
  className?: string;
}

/**
 * Shared two-branch error UI for partner pages: expected errors (403 "not
 * linked to HubSpot" / "admin only") render a friendly icon panel, anything
 * else the generic load error with an optional retry button.
 */
export function PartnerErrorState({
  error,
  icon,
  title,
  description,
  isExpectedError = isForbiddenError,
  onRetry,
  className
}: PartnerErrorStateProps) {
  const { t } = useTranslation();

  if (isExpectedError(error)) {
    return (
      <PartnerInfoPanel icon={icon} title={title} description={description} className={className} />
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center flex-1 gap-3 text-center',
        className
      )}
    >
      <p className="font-medium text-destructive">{t('partner.common.loadError')}</p>
      {onRetry && (
        <Button variant="outline" onClick={onRetry}>
          {t('partner.common.retry')}
        </Button>
      )}
    </div>
  );
}
